import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import noop from 'lodash/noop';
import { Box, Flex } from 'roo-ui/components';
import Image from 'components/Image';

export const ThumbnailTray = styled(Flex)`
  display: inline-flex;
  transition: transform 0.45s ease-out;
  transform: translate3d(${(props) => props.offset}, 0, 0);
`;

export const ThumbnailButton = styled('button')`
  outline: none;
  border: none;
  position: relative;
  padding: 0;
  margin-right: 2px;
  background-color: transparent;
  flex: 1 0 auto;
  cursor: pointer;
  opacity: ${(props) => (props.selected ? 1 : 0.8)};
  &:focus {
    border: 3px solid ${themeGet('colors.brand.secondary')};
    opacity: 0.9;
  }
`;

const getAltText = (caption) => `${caption || 'Unlabelled image'} thumbnail`;

const Thumbnails = ({ index, images, onClick, width, thumbnailWidth, thumbnailHeight, ...rest }) => {
  const thumbnailsRef = useRef();
  const [trayOffset, setTrayOffset] = useState(0);

  // TODO: recalculate on screen resize
  useEffect(() => {
    const totalScrollWidth = thumbnailsRef.current.scrollWidth - thumbnailsRef.current.clientWidth;
    const perIndexScroll = totalScrollWidth / (images.length - 1);
    const offset = index * perIndexScroll;
    setTrayOffset(`-${offset}px`);
  }, [images, index]);

  const handleOnClick = useCallback((id) => onClick.bind(null, id), [onClick]);

  return (
    <Box as="nav" ref={thumbnailsRef} width={width} overflow="hidden" {...rest}>
      <ThumbnailTray offset={trayOffset}>
        {images.map(({ urlSmall, caption }, id) => (
          <Image
            as={ThumbnailButton}
            key={id}
            src={urlSmall}
            alt={getAltText(caption)}
            onClick={handleOnClick(id)}
            width={thumbnailWidth}
            height={thumbnailHeight}
            selected={id === index}
          />
        ))}
      </ThumbnailTray>
    </Box>
  );
};

Thumbnails.propTypes = {
  index: PropTypes.number,
  images: PropTypes.array,
  onClick: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
  thumbnailWidth: PropTypes.number,
  thumbnailHeight: PropTypes.number,
};

Thumbnails.defaultProps = {
  index: 0,
  images: [],
  onClick: noop,
  width: '100%',
  thumbnailWidth: 90,
  thumbnailHeight: 90,
};

export default Thumbnails;
